import { and, reads } from '@ember/object/computed';
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { A as emberA } from '@ember/array';
import { isIOSMinNativeDocVersion, isMobileApp } from 'client-portal/utils/mobile-detect';
import { inject as service } from '@ember/service';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import classic from 'ember-classic-decorator';

@classic
export default class PracticeModel extends Model {
  @service session;

  @belongsTo('office', { async: false }) defaultOffice;
  @belongsTo('office', { async: false }) defaultMarketingOffice;
  @belongsTo('phone', { async: false }) defaultPhone;
  @belongsTo('practice-website', { async: false }) practiceWebsite;
  @hasMany('clinician', { async: false }) clinicians;
  @attr() fullName;
  @attr() practiceUrl;
  @attr() schedulingWidgetUrl;
  @attr() schedulingWidgetScopeId;
  @attr() schedulingWidgetApplicationId;
  @attr() mixpanelKey;
  @attr() customStripePublishableKey;
  @attr() currency;
  @attr('boolean') hasCustomDomain;
  @attr('boolean') clientPortalEnabled;
  @attr('boolean') selfSchedulingEnabled;
  @attr('boolean') isNewClientAllowedToBookAppt;
  @attr('boolean') isNewClientAllowedToJoinWaitlist;
  @attr('boolean') isNewContactsAllowedToBookAppt;
  @attr('boolean') isNewCouplesAllowedToBookAppt;
  @attr('boolean') isNewIndividualClientsAllowedToBookAppt;
  @attr('boolean') requireCreditCardForNewClients;
  @attr('boolean') documentsUpload;
  @attr('number') selfScheduleMinimumLeadTime;
  @attr('boolean') hasCustomStripeAccount;
  @attr() stripeConnectedAccountId;
  @attr('boolean') isClientAllowedToConfirmAppt;
  @attr('boolean') isClientAllowedToCancelAppt;
  @attr() timeZone;
  @attr('number') clientCancellableHrs;
  @attr('boolean') websiteEnabled;
  @attr('boolean') websitePublished;
  @attr('boolean') telehealthEnabled;
  @attr() recaptchaSiteKey;
  @attr('boolean') featureAddProductsToGfe;
  @attr('boolean') featureClientappDocumentAccess;
  @attr('boolean') featureClientappDocumentsUpdate;
  @attr('boolean') featureIosClientappPromotionBanner;
  @attr('boolean') isMeasurementMhqolEnabled;
  @attr('boolean') isMeasurementYbocsEnabled;
  @attr('boolean') isMeasurementWho5Enabled;
  @attr('boolean') featureClientPortalLegalAgreements;
  @attr('boolean') featureClientPortalInsuranceEffectiveDates;
  @attr('boolean') featureCreditDebitMix;
  @attr('boolean') featureInsuranceTextRecognition;
  @attr('boolean') featureContactFormCustomSettings;
  @attr('boolean') featureScheduledMaintenance;
  @attr('boolean') announcementsAvailable;
  @attr('boolean') featureEnableClientPortalPrescreenerSettings;
  @attr('boolean') showPrescreenerSection;
  @attr('boolean') showPaymentSelection;
  @attr('boolean') featureSecureMessagingEmber;
  @attr('boolean') practiceContactForm;
  @attr('boolean') monarchContactForm;
  @attr('boolean') featureLaWildfirePrescreenerBanner;
  @attr('boolean') featurePaymentLinks;
  @attr('boolean') featurePaymentLinksSms;
  @attr('boolean') featurePaymentMethodConfiguration;
  @attr('boolean') featureStripeAchPayments;
  @attr('string') dataCollectionMandateText;
  @attr('string') chargeExecutionMandateText;
  @attr('boolean') claimAssistantSettingActive;
  @attr('boolean') insuranceInfoDocumentRequestEnabled;
  @attr('boolean') isLocalizationEnabled;
  @attr('string') featureThirdPartyBilling;
  @attr('boolean') featureDatagrailSegmentConsentClients;
  @attr('boolean') featureStripeLinkPassthroughMode;
  @attr('boolean') featurePhotoIdUpload;
  @attr('boolean') autoAcceptAppointmentRequests;
  @attr('boolean') autoAcceptNewClientAppointmentRequests;
  @attr('boolean') clinicianFilteringEnabled;
  @attr('boolean') clinicianFilteringV2Enabled;

  @and('clientPortalEnabled', 'selfSchedulingEnabled') effectiveSelfScheduling;
  @and('effectiveSelfScheduling', 'isNewClientAllowedToBookAppt') requestAsNewClient;

  @reads('defaultPhone.number') defaultPhoneNumber;

  @dependentKeyCompat
  get useBookAppointmentLanguage() {
    return this.session.isNewClientBookingFlow
      ? this.autoAcceptNewClientAppointmentRequests
      : this.autoAcceptAppointmentRequests;
  }

  @computed('defaultPhoneNumber')
  get cleanPhoneNumber() {
    let { defaultPhoneNumber } = this;
    return defaultPhoneNumber ? defaultPhoneNumber.replace(/[^\d+]+/g, '') : defaultPhoneNumber;
  }

  @computed(
    'isNewContactsAllowedToBookAppt',
    'isNewCouplesAllowedToBookAppt',
    'isNewIndividualClientsAllowedToBookAppt'
  )
  get clientTypesAllowedToBookAppt() {
    return [
      (this.isNewIndividualClientsAllowedToBookAppt && 'client') || null,
      (this.isNewCouplesAllowedToBookAppt && 'couple') || null,
      (this.isNewContactsAllowedToBookAppt && 'contact') || null,
    ].compact();
  }

  @computed('featureClientappDocumentsUpdate')
  get canUseNativeDocRendering() {
    return this.featureClientappDocumentsUpdate && isIOSMinNativeDocVersion();
  }

  @computed('featureClientappDocumentAccess')
  get hasClientappDocumentAccess() {
    return this.featureClientappDocumentAccess && isMobileApp();
  }

  static fieldsFor(context) {
    let fields = emberA();
    this.fields.forEach((_, v) => fields.pushObject(v));
    fields.removeObject('meta');
    fields.removeObjects(['featureBookingWidgetDisplaySpecialties']);
    // Clinicians are loaded via /clinicians or /clinician-search, not environment.
    if (context === 'environment') {
      fields.removeObject('clinicians');
    }
    return fields;
  }
}
